import { useState, useRef, useEffect } from 'react';
import api from '../api';
import CatLoader from '../components/CatLoader';

const REGIONS = ['Южный', 'Центральный', 'Поволжье', 'Урал', 'Северо-Запад'];
const WAREHOUSES = {
  'Южный': 'Невинномысск',
  'Центральный': 'Рязань (Тюшевское)',
  'Поволжье': 'Самара (Новосемейкино)',
  'Урал': 'Екатеринбург - Перспективная 14',
  'Северо-Запад': 'СПБ Шушары',
};

function FileZone({ label, hint, file, onFile }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  return (
    <div
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => ref.current.click()}
      style={{
        border: `2px dashed ${drag ? 'var(--accent)' : file ? '#10b981' : 'var(--border)'}`,
        borderRadius: 12, padding: '28px 24px', textAlign: 'center', cursor: 'pointer',
        background: drag ? 'rgba(59,130,246,0.05)' : file ? 'rgba(16,185,129,0.05)' : 'var(--surface)',
        transition: 'all 0.2s', flex: 1,
      }}
    >
      <div style={{ fontSize: 32, marginBottom: 8 }}>{file ? '✅' : '📂'}</div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4, color: file ? '#10b981' : 'var(--text)' }}>
        {file ? file.name : label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{file ? 'Нажмите чтобы заменить' : hint}</div>
      <input ref={ref} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={e => onFile(e.target.files[0])} />
    </div>
  );
}

export default function FFTask() {
  const [regionFile, setRegionFile] = useState(null);
  const [ffFile, setFFFile] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [lastInfo, setLastInfo] = useState(null);

  useEffect(() => {
    api.get('/ff-task/last').then(r => {
      if (r.data) {
        setResult(r.data.summary);
        setFileData({ base64: r.data.file, filename: r.data.filename });
        setLastInfo({ uploaded_by: r.data.uploaded_by, created_at: r.data.created_at });
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const canGenerate = regionFile && ffFile && deliveryDate;

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const form = new FormData();
      form.append('regionFile', regionFile);
      form.append('ffFile', ffFile);
      form.append('deliveryDate', deliveryDate);
      const { data } = await api.post('/ff-task/generate', form);
      setResult(data.summary);
      setFileData({ base64: data.file, filename: data.filename });
      setLastInfo(null);
      setRegionFile(null);
      setFFFile(null);
    } catch(e) {
      setError(e.response?.data?.error || e.message);
    } finally { setGenerating(false); }
  };

  const download = () => {
    if (!fileData) return;
    const bin = atob(fileData.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fileData.filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <CatLoader loading={true} />;

  return (
    <>
      <CatLoader loading={generating} />
      <div style={{ padding: '24px 32px', maxWidth: 900 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Подсорт (ТЗ для ФФ)</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 28 }}>
          Загрузите файл поставок по регионам и файл ФФ — получите готовое ТЗ для поставки
        </p>

        {/* Загрузка файлов — показываем только если нет результата или хотим обновить */}
        {(!result || regionFile || ffFile) && (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <FileZone
                label="Поставки по регионам"
                hint="Аналитика → Поставки по регионам → Детализация"
                file={regionFile}
                onFile={setRegionFile}
              />
              <FileZone
                label="Файл ФФ"
                hint="Файл с остатками ФФ и транзитом"
                file={ffFile}
                onFile={setFFFile}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Дата поставки на WB</div>
                <input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }} />
              </div>
              <button onClick={generate} disabled={!canGenerate || generating}
                style={{
                  marginTop: 22, padding: '9px 28px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: canGenerate ? 'var(--accent)' : 'rgba(255,255,255,0.05)',
                  color: canGenerate ? '#fff' : 'var(--text-muted)',
                  border: 'none', cursor: canGenerate ? 'pointer' : 'default', transition: 'all 0.2s',
                }}>
                {generating ? '⏳ Генерируем...' : '⚡ Сгенерировать ТЗ'}
              </button>
            </div>
          </>
        )}

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#ef4444' }}>
            {error}
          </div>
        )}

        {/* Результат */}
        {result && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>ТЗ готово 🎉</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Дата поставки: {result.deliveryDate} · {result.positions} позиций
                  {lastInfo && <span> · сгенерировано {lastInfo.uploaded_by} · {new Date(lastInfo.created_at).toLocaleString('ru-RU', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setRegionFile(null); setFFFile(null); }} style={{
                  padding: '9px 16px', borderRadius: 8, fontSize: 13,
                  background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border)',
                  color: 'var(--text-muted)', cursor: 'pointer',
                }}>↩ Обновить</button>
                <button onClick={download} style={{
                  padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                  background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)',
                  color: '#10b981', cursor: 'pointer',
                }}>⬇ Скачать Excel</button>
              </div>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: '14px 20px', minWidth: 140 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Всего к отправке</div>
                <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--accent)' }}>{result.total} шт</div>
              </div>
              {REGIONS.map(reg => (
                result.byRegion[reg] > 0 && (
                  <div key={reg} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 20px', minWidth: 160 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{reg}</div>
                    <div style={{ fontSize: 11, color: 'rgba(200,215,235,0.4)', marginBottom: 6 }}>{WAREHOUSES[reg]}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{result.byRegion[reg]} шт</div>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {/* Кнопка создать новое если есть результат */}
        {result && !regionFile && !ffFile && (
          <button onClick={() => { setRegionFile(null); setFFFile(null); setResult(null); setFileData(null); }}
            style={{ marginTop: 16, padding: '8px 20px', borderRadius: 8, fontSize: 13, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>
            + Создать новое ТЗ
          </button>
        )}
      </div>
    </>
  );
}
